import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { observer } from "mobx-react";
import moment from "moment";
import { Button, Card, Icon, SegmentedControl } from "@playerelite/ui";
import { UserStore } from "../..";
import BreadCrumbs from "../../components/breadcrumsb";
import { runAuthHandler } from "../util";
import {
  changeDirection,
  formatChange,
  formatCount,
  formatDecimal,
  mean,
  mergeByDay,
  percentChange,
  Periods,
  Point,
  RangeDays,
  RANGES,
  retentionAverages,
  retentionRows,
  splitPeriods,
  sum,
  toCsv,
  values,
} from "./analytics-utils";
import ChartCard, { cardStatus } from "./ChartCard";
import DeviceSplit from "./DeviceSplit";
import KpiCard from "./KpiCard";
import RetentionHeatmap from "./RetentionHeatmap";
import TimeSeriesChart, { ChartSeries, ChartTone } from "./TimeSeriesChart";
import {
  useDaySeries,
  useDeviceBreakdown,
  useRetention,
  useTotalStats,
} from "./use-analytics";

const RANGE_OPTIONS = RANGES.map((days) => ({ value: String(days), label: `${days} days` }));

/** Fills the selected range with the action colour. The kit's pale tint is easy to miss. */
const RANGE_SELECTED_CLASS =
  "[&>[data-state=on]]:!bg-action [&>[data-state=on]]:!text-action-fg [&>[data-state=on]]:font-semibold [&>[data-state=on]:hover]:!bg-action-hover";

const ACTIVE_SERIES: ChartSeries[] = [
  { key: "activeUsers", name: "Daily active", tone: "primary", kind: "line" },
  { key: "geoActiveUsers", name: "Geo-active", tone: "secondary", kind: "line" },
];

type Summary = { current: number; change: number | null; unit: "%" | " pts" };

/** The period's total or average, and its change against the previous period when there is one. */
function summarise(periods: Periods | undefined, by: "sum" | "mean", unit: "%" | " pts") {
  if (!periods) return undefined;
  const reduce = by === "sum" ? sum : mean;
  const current = reduce(values(periods.current));
  if (!periods.previous) return { current, change: null, unit };
  const previous = reduce(values(periods.previous));
  return {
    current,
    change: unit === "%" ? percentChange(current, previous) : current - previous,
    unit,
  };
}

const chartData = (periods: Periods | undefined) =>
  (periods?.current ?? []).map((p) => ({ label: p.label, value: p.value }));

const sinceText = (epoch: number | undefined) =>
  epoch ? `since ${moment(epoch).format("D MMM YYYY")}` : null;

function downloadCsv(filename: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const userStore = useContext(UserStore);
  const venue = userStore.selectedVenue;
  const venueId = venue?.venueId;
  const [range, setRange] = useState<RangeDays>(30);

  useEffect(() => {
    runAuthHandler({ userStore, navigate });
  }, [venueId]);

  const signups = useDaySeries("signups", venueId, range);
  const appOpens = useDaySeries("appOpens", venueId, range);
  const activeUsers = useDaySeries("activeUsers", venueId, range);
  const geoActiveUsers = useDaySeries("geoActiveUsers", venueId, range);
  const sessionsPerUser = useDaySeries("sessionsPerUser", venueId, range);
  const stickiness = useDaySeries("stickiness", venueId, range);
  const totals = useTotalStats(venueId);
  const devices = useDeviceBreakdown(venueId);
  const retention = useRetention(venueId);

  const periods = useMemo(() => {
    const split = (data: Point[] | undefined) => (data ? splitPeriods(data, range) : undefined);
    return {
      signups: split(signups.data),
      appOpens: split(appOpens.data),
      activeUsers: split(activeUsers.data),
      geoActiveUsers: split(geoActiveUsers.data),
      sessionsPerUser: split(sessionsPerUser.data),
      stickiness: split(stickiness.data),
    };
  }, [
    range,
    signups.data,
    appOpens.data,
    activeUsers.data,
    geoActiveUsers.data,
    sessionsPerUser.data,
    stickiness.data,
  ]);

  const periodLabel = `${range} days`;

  const kpis: {
    label: string;
    summary?: Summary;
    format: (n: number) => string;
    trend: number[];
    tone: ChartTone;
    isError: boolean;
  }[] = [
    {
      label: "New sign-ups",
      summary: summarise(periods.signups, "sum", "%"),
      format: formatCount,
      trend: values(periods.signups?.current ?? []),
      tone: "primary",
      isError: signups.isError,
    },
    {
      label: "Avg daily active",
      summary: summarise(periods.activeUsers, "mean", "%"),
      format: formatCount,
      trend: values(periods.activeUsers?.current ?? []),
      tone: "primary",
      isError: activeUsers.isError,
    },
    {
      label: "App opens",
      summary: summarise(periods.appOpens, "sum", "%"),
      format: formatCount,
      trend: values(periods.appOpens?.current ?? []),
      tone: "primary",
      isError: appOpens.isError,
    },
    {
      label: "Avg geo-active",
      summary: summarise(periods.geoActiveUsers, "mean", "%"),
      format: formatCount,
      trend: values(periods.geoActiveUsers?.current ?? []),
      tone: "secondary",
      isError: geoActiveUsers.isError,
    },
    {
      label: "Stickiness",
      summary: summarise(periods.stickiness, "mean", " pts"),
      format: (n) => `${formatDecimal(n, 1)}%`,
      trend: values(periods.stickiness?.current ?? []),
      tone: "primary",
      isError: stickiness.isError,
    },
  ];

  const activeData = useMemo(
    () =>
      mergeByDay([
        periods.activeUsers?.current ?? [],
        periods.geoActiveUsers?.current ?? [],
      ]).map((d) => ({
        label: d.label,
        activeUsers: d.values[0],
        geoActiveUsers: d.values[1],
      })),
    [periods.activeUsers, periods.geoActiveUsers]
  );

  const retentionView = useMemo(() => {
    const rows = retention.data ?? [];
    return {
      rows: retentionRows(rows),
      averages: retentionAverages(rows),
      averageSignUps: Math.round(mean(rows.map((r) => r.signUpCount))),
    };
  }, [retention.data]);

  const exportSeries = [
    { name: "Sign-ups", periods: periods.signups },
    { name: "App opens", periods: periods.appOpens },
    { name: "Daily active", periods: periods.activeUsers },
    { name: "Geo-active", periods: periods.geoActiveUsers },
    { name: "Sessions per active member", periods: periods.sessionsPerUser },
    { name: "Stickiness (%)", periods: periods.stickiness },
  ];
  const canExport = exportSeries.every((s) => s.periods);

  const exportCsv = () => {
    const merged = mergeByDay(exportSeries.map((s) => s.periods?.current ?? []));
    const csv = toCsv(
      ["Date", ...exportSeries.map((s) => s.name)],
      merged.map((d) => [d.day, ...d.values.map((v) => v ?? "")])
    );
    downloadCsv(`analytics-${venueId}-${range}-days-${moment().format("YYYY-MM-DD")}.csv`, csv);
  };

  const singleSeries = (
    title: string,
    description: string,
    query: typeof signups,
    periodsFor: Periods | undefined,
    summary: string | undefined,
    series: ChartSeries,
    height: number,
    formatValue?: (n: number) => string
  ) => (
    <ChartCard
      title={title}
      description={description}
      summary={summary}
      status={cardStatus([query], !periodsFor?.current.length)}
      onRetry={() => query.refetch()}
      bodyHeight={height}
    >
      <TimeSeriesChart
        data={chartData(periodsFor)}
        series={[series]}
        height={height}
        formatValue={formatValue}
      />
    </ChartCard>
  );

  return (
    <section className="bg-surface-sunken text-fg">
      <div className="mx-auto flex max-w-screen-xl flex-col gap-6 px-4 py-8 lg:px-6">
        <BreadCrumbs
          routes={[
            { path: "/home", breadcrumbName: "Home" },
            { path: "/analytics", breadcrumbName: "Analytics" },
          ]}
        />

        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">Analytics</h1>
            <p className="text-sm text-fg-muted">
              {venue?.displayName ? `${venue.displayName} · ` : ""}app members and engagement
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              aria-label="Date range"
              options={RANGE_OPTIONS}
              value={String(range)}
              onValueChange={(v) => setRange(Number(v) as RangeDays)}
              className={RANGE_SELECTED_CLASS}
            />
            <Button
              variant="adminWhite"
              startIcon={<Icon name="download" />}
              disabled={!canExport}
              onClick={exportCsv}
            >
              Export CSV
            </Button>
          </div>
        </header>

        <section
          aria-label="Key metrics"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5"
        >
          {kpis.map((k) => (
            <KpiCard
              key={k.label}
              label={k.label}
              value={k.summary ? k.format(k.summary.current) : undefined}
              change={
                k.summary?.change != null ? formatChange(k.summary.change, k.summary.unit) : null
              }
              direction={changeDirection(k.summary?.change ?? 0)}
              periodLabel={periodLabel}
              trend={k.trend}
              tone={k.tone}
              isError={k.isError}
            />
          ))}
        </section>

        <Card padding="none" className="overflow-hidden">
          <h2 className="border-b border-border bg-surface-sunken px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-fg-muted">
            All time
          </h2>
          {totals.data ? (
            <div className="grid grid-cols-1 divide-y divide-border md:grid-cols-3 md:divide-x md:divide-y-0">
              {[
                { value: totals.data.totalUsers, noun: "members", since: totals.data.totalUsersSinceDate },
                { value: totals.data.totalDevices, noun: "devices", since: totals.data.totalDevicesSinceDate },
                { value: totals.data.totalAppOpens, noun: "app opens", since: totals.data.totalAppOpensSinceDate },
              ].map((t) => (
                <div key={t.noun} className="flex flex-col gap-1 px-6 py-5">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-mono text-4xl font-semibold tabular-nums text-fg">
                      {formatCount(t.value)}
                    </span>
                    <span className="text-xl font-medium text-fg">{t.noun}</span>
                  </div>
                  <span className="text-sm text-fg-subtle">{sinceText(t.since)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="px-6 py-5 text-base text-fg-muted">
              {totals.isError ? "Couldn't load all-time totals." : "Loading…"}
            </p>
          )}
        </Card>

        <ChartCard
          title="Active members"
          description="Members who opened the app each day, and members whose app reported their location."
          status={cardStatus([activeUsers, geoActiveUsers], activeData.length === 0)}
          onRetry={() => {
            activeUsers.refetch();
            geoActiveUsers.refetch();
          }}
          bodyHeight={300}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
              {ACTIVE_SERIES.map((s) => (
                <span key={s.key} className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={`h-3 w-3 rounded-sm ${s.tone === "primary" ? "bg-action" : "bg-warning"}`}
                  />
                  <span className="text-fg-muted">{s.name}</span>
                  <strong className="font-mono font-semibold tabular-nums">
                    {formatCount(
                      mean(values(periods[s.key as "activeUsers" | "geoActiveUsers"]?.current ?? []))
                    )}{" "}
                    avg
                  </strong>
                </span>
              ))}
            </div>
            <TimeSeriesChart data={activeData} series={ACTIVE_SERIES} height={260} />
          </div>
        </ChartCard>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {singleSeries(
            "Daily sign-ups",
            "New members who finished sign-up. Members re-registering on another device aren't counted again.",
            signups,
            periods.signups,
            periods.signups ? `${formatCount(sum(values(periods.signups.current)))} total` : undefined,
            { key: "value", name: "Sign-ups", tone: "primary", kind: "bar" },
            220
          )}
          {singleSeries(
            "Daily app opens",
            "App sessions across all members. Spikes usually follow a push notification.",
            appOpens,
            periods.appOpens,
            periods.appOpens ? `${formatCount(sum(values(periods.appOpens.current)))} total` : undefined,
            { key: "value", name: "App opens", tone: "primary", kind: "bar" },
            220
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {singleSeries(
            "Sessions per active member",
            "How many times an active member opens the app in a day.",
            sessionsPerUser,
            periods.sessionsPerUser,
            periods.sessionsPerUser
              ? `${formatDecimal(mean(values(periods.sessionsPerUser.current)), 2)} avg`
              : undefined,
            { key: "value", name: "Sessions", tone: "primary", kind: "line" },
            180,
            (n) => formatDecimal(n, 1)
          )}
          {singleSeries(
            "Stickiness",
            "Daily active ÷ monthly active members. Higher means members come back on more days each month.",
            stickiness,
            periods.stickiness,
            periods.stickiness
              ? `${formatDecimal(mean(values(periods.stickiness.current)), 1)}%`
              : undefined,
            { key: "value", name: "Stickiness", tone: "primary", kind: "area" },
            180,
            (n) => `${formatDecimal(n, 0)}%`
          )}
          <ChartCard
            title="Devices"
            description="Registered member devices by platform, all time."
            status={cardStatus([devices], false)}
            onRetry={() => devices.refetch()}
            bodyHeight={180}
            className="md:col-span-2 xl:col-span-1"
          >
            {devices.data ? (
              <DeviceSplit
                ios={devices.data.iOSDevicesCount}
                android={devices.data.androidDevicesCount}
              />
            ) : null}
          </ChartCard>
        </div>

        <ChartCard
          title="New member retention"
          description="Of the members who signed up each day, the share who opened the app again N days later."
          status={cardStatus([retention], retentionView.rows.length === 0)}
          onRetry={() => retention.refetch()}
          bodyHeight={240}
        >
          <RetentionHeatmap
            rows={retentionView.rows}
            averages={retentionView.averages}
            averageSignUps={retentionView.averageSignUps}
          />
        </ChartCard>
      </div>
    </section>
  );
};

export default observer(AnalyticsPage);
